import { useEffect, useState } from "react";
import { HALLOWEEN_MODE } from "./halloweenMode";

const STORAGE_KEY = "audassist.theme";
const ICON_VERSION = "7";

function systemPrefersDark() {
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function storedPreference() {
  try { return localStorage.getItem(STORAGE_KEY) || "auto"; } catch { return "auto"; }
}

function applyFavicon(dark) {
  const defaults = dark ? "/dark_theme.png" : "/favicon.svg";
  const href = HALLOWEEN_MODE
    ? `/favicon-halloween-32.png?v=${ICON_VERSION}`
    : `${defaults}?v=${ICON_VERSION}`;

  document.querySelectorAll("link[rel='icon'],link[rel='shortcut icon']")
    .forEach(link => { link.href = href; });

  let apple = document.querySelector("link[rel='apple-touch-icon']");
  if (!apple) {
    apple = document.createElement("link");
    apple.rel = "apple-touch-icon";
    document.head.appendChild(apple);
  }
  apple.href = HALLOWEEN_MODE
    ? `/apple-touch-icon-halloween.png?v=${ICON_VERSION}`
    : `/AudAssist.ico?v=${ICON_VERSION}`;

  let manifest = document.querySelector("link[rel='manifest']");
  if (!manifest) {
    manifest = document.createElement("link");
    manifest.rel = "manifest";
    document.head.appendChild(manifest);
  }
  manifest.href = HALLOWEEN_MODE
    ? `/manifest-halloween.json?v=${ICON_VERSION}`
    : `/manifest.json?v=${ICON_VERSION}`;
}

function applyThemeColor(dark) {
  let meta = document.querySelector("meta[name='theme-color']");
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "theme-color";
    document.head.appendChild(meta);
  }
  meta.content = dark ? "#1a1b1f" : "#ffffff";
  document.documentElement.style.background = dark ? "#1a1b1f" : "#eef2f7";
}

function applyClass(pref) {
  const dark = pref === "dark" || (pref === "auto" && systemPrefersDark());
  document.documentElement.classList.toggle("dark", dark);
  applyFavicon(dark);
  applyThemeColor(dark);
  return dark;
}

export function useTheme() {
  const [preference, _setPreference] = useState(storedPreference);
  const [isDark, setIsDark] = useState(() => applyClass(storedPreference()));

  function setPreference(pref) {
    _setPreference(pref);
    try { localStorage.setItem(STORAGE_KEY, pref); } catch {}
    setIsDark(applyClass(pref));
  }

  useEffect(() => {
    if (preference !== "auto") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => setIsDark(applyClass("auto"));
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [preference]);

  useEffect(() => {
    const html = document.documentElement;
    const sync = () => setIsDark(html.classList.contains("dark"));
    const observer = new MutationObserver(sync);
    observer.observe(html, { attributes: true, attributeFilter: ["class"] });
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) {
        const pref = e.newValue || "auto";
        _setPreference(pref);
        setIsDark(applyClass(pref));
      }
    };
    window.addEventListener("storage", onStorage);
    return () => {
      observer.disconnect();
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  return { preference, setPreference, isDark };
}
